<div
  #panel
  class="image-ai-panel attribute-editor-show-from-right"
  role="region"
  aria-label="Generate with AI"
>
  <div class="image-ai-content">
    @if (view === 'main') {
      <section class="image-ai-section">
        <textarea
          #promptInput
          id="image-ai-prompt"
          class="image-ai-prompt"
          rows="4"
          [(ngModel)]="prompt"
          [disabled]="loading"
          placeholder="What would you like to generate?"
          aria-label="What would you like to generate?"
          (keydown.enter)="onPromptEnter($event)"
        ></textarea>
        <button
          type="button"
          class="image-ai-generate-button"
          [class.loading]="loading"
          [disabled]="!canGenerate()"
          [attr.aria-busy]="loading"
          (click)="onGenerate()"
        >
          @if (loading) {
            <span class="image-ai-spinner" aria-hidden="true"></span>
            <span class="tw-sr-only">Generate image loading</span>
          } @else {
            <span>Generate image</span>
          }
        </button>

        @if (messageType) {
          <div
            class="image-ai-message"
            [class.error]="isErrorMessage()"
            aria-live="polite"
          >
            @switch (messageType) {
              @case ('no_credits') {
                <span>You don't have enough credits. </span>
                @if (canAccessCompanySettings()) {
                  <a
                    class="image-ai-message-link"
                    [uiSref]="'apps.settings.company-details'"
                  >
                    See my credits.
                  </a>
                }
              }
              @case ('default') {
                <span>Image generation uses credits. </span>
                @if (canAccessCompanySettings()) {
                  <a
                    class="image-ai-message-link"
                    [uiSref]="'apps.settings.company-details'"
                  >
                    See my credits.
                  </a>
                }
              }
              @case ('error') {
                <span>Something went wrong. Please try again.</span>
              }
              @case ('content_blocked') {
                <span>
                  This request can't be generated because it violates our
                  content policy.
                </span>
              }
            }
          </div>
        }
      </section>

      @if (loading || generations.length > 0) {
        <section
          class="image-ai-section image-ai-grid-section"
          aria-labelledby="recent-generations"
        >
          <h3 id="recent-generations" class="image-ai-section-title">
            Recent Generations
          </h3>
          <div
            class="image-ai-grid"
            scrollingList
            (scrollEndEvent)="loadMore()"
          >
            <div class="image-ai-grid-columns">
              <div class="image-ai-grid-column">
                @if (loading) {
                  <div
                    class="image-ai-grid-item image-ai-grid-placeholder ai-loading-background-pulse"
                    [style.aspect-ratio]="placeholderAspectRatio"
                  >
                    <div
                      class="image-ai-glow-container ai-loading-glow-container"
                      aria-hidden="true"
                    >
                      <div
                        class="image-ai-glow-spinner ai-loading-glow-spinner"
                      >
                        <div
                          class="image-ai-glow-circle image-ai-glow-circle-blue ai-loading-glow-circle ai-loading-glow-circle-fill-height ai-loading-glow-circle-blue"
                        ></div>
                        <div
                          class="image-ai-glow-circle image-ai-glow-circle-green ai-loading-glow-circle ai-loading-glow-circle-fill-height ai-loading-glow-circle-green"
                        ></div>
                      </div>
                    </div>
                    <span role="status" class="tw-sr-only"
                      >Generating image…</span
                    >
                  </div>
                }
                @for (generation of leftColumn; track generation.url) {
                  <ng-container
                    *ngTemplateOutlet="tile; context: { $implicit: generation }"
                  ></ng-container>
                }
              </div>
              <div class="image-ai-grid-column">
                @for (generation of rightColumn; track generation.url) {
                  <ng-container
                    *ngTemplateOutlet="tile; context: { $implicit: generation }"
                  ></ng-container>
                }
              </div>
            </div>
          </div>

          <ng-template #tile let-generation>
            <div
              class="image-ai-grid-item"
              [class.image-ai-grid-item-loading]="!generation.loaded"
            >
              <button
                type="button"
                class="image-ai-grid-select"
                (click)="onSelectGeneration(generation)"
              >
                @if (!generation.loaded) {
                  <span
                    class="image-ai-grid-skeleton ai-loading-background-pulse"
                    aria-hidden="true"
                  ></span>
                }
                <img
                  class="image-ai-grid-image"
                  [class.img-contain]="generation.previewFailed"
                  [class.image-ai-grid-image-enter]="generation.justGenerated"
                  [class.is-loaded]="generation.loaded"
                  [src]="generation.displaySrc"
                  [alt]="generation.filename"
                  loading="lazy"
                  decoding="async"
                  (load)="onImageLoad(generation)"
                  (error)="onPreviewError(generation)"
                />
              </button>

              @if (generation.justGenerated) {
                <div class="image-ai-feedback-actions">
                  <button
                    type="button"
                    class="image-ai-feedback-button"
                    [class.image-ai-feedback-button-active-up]="
                      generation.feedbackRating === 'good'
                    "
                    aria-label="Good result"
                    [attr.aria-pressed]="generation.feedbackRating === 'good'"
                    (click)="onThumbUp(generation, $event)"
                  >
                    <mat-icon
                      class="image-ai-feedback-icon small thin"
                      svgIcon="thumbs-up"
                      aria-hidden="true"
                    ></mat-icon>
                  </button>
                  <button
                    type="button"
                    class="image-ai-feedback-button"
                    [class.image-ai-feedback-button-active]="
                      generation.feedbackRating === 'bad'
                    "
                    aria-label="Bad result"
                    [attr.aria-pressed]="generation.feedbackRating === 'bad'"
                    (click)="onThumbDown(generation, $event)"
                  >
                    <mat-icon
                      class="image-ai-feedback-icon small thin"
                      svgIcon="thumbs-down"
                      aria-hidden="true"
                    ></mat-icon>
                  </button>
                </div>
              }
            </div>
          </ng-template>
        </section>
      }
    }

    @if (view === 'feedback') {
      <section class="image-ai-section image-ai-feedback-section">
        <p class="image-ai-feedback-text">
          Sorry you didn't like this. Please tell us why:
        </p>
        <textarea
          #feedbackInput
          id="image-ai-feedback"
          class="image-ai-prompt"
          rows="4"
          [(ngModel)]="feedbackText"
          placeholder="Send feedback..."
          aria-label="Send feedback"
          (keydown.enter)="onFeedbackEnter($event)"
        ></textarea>
        <button
          type="button"
          class="image-ai-generate-button"
          [disabled]="!canSendFeedback()"
          (click)="onSendFeedback()"
        >
          <span>Send feedback</span>
        </button>
        <button
          rv-button
          variant="secondary"
          type="button"
          class="tw-w-full"
          (click)="onFeedbackBack()"
        >
          Cancel and go back
        </button>
      </section>
    }

    @if (view === 'feedback-sent') {
      <section class="image-ai-section image-ai-feedback-section">
        <p class="image-ai-feedback-text" role="status" aria-live="polite">
          Thank you! Your feedback has been sent to our team for review
        </p>
        <button
          rv-button
          variant="secondary"
          type="button"
          class="tw-w-full"
          (click)="onFeedbackBack()"
        >
          Go back
        </button>
      </section>
    }
  </div>
</div>
